import { DiscussionType, Message } from "@cnx/common";
import { type ReactNode, useEffect, useRef } from "react";
import { useAuthStore } from "stores/authStore";
import { formatChatTime } from "utils/chat";
import IncomingMessage from "./IncomingMessage";
import MessageAttachment from "./MessageAttachment";
import PrivateMessage from "./PrivateMessage";
import UserMessage from "./UserMessage";

type ChatMessageItemProps = {
  item: Message;
  discussionType?: DiscussionType;
  isSelected?: boolean;
  onSelect?: (message: Message) => void;
};

const ChatMessageItem = ({
  item,
  discussionType,
  isSelected,
  onSelect,
}: ChatMessageItemProps) => {
  const currentUserId = useAuthStore(state => state.currentUserId);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressStartPos = useRef<{ x: number; y: number } | null>(null);
  const longPressFired = useRef(false);

  const time = item.createdTime
    ? formatChatTime(item.createdTime.toString())
    : null;

  const isOwn = item.createdBy === currentUserId;
  const isEdited = !!item.modifiedTime;
  const isDeleted = item.archived === 1;

  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  const handlePressStart = (x: number, y: number) => {
    if (isDeleted) return;
    longPressFired.current = false;
    pressStartPos.current = { x, y };
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onSelect?.(item);
    }, 500);
  };

  const handlePressMove = (x: number, y: number) => {
    if (!pressStartPos.current || longPressFired.current) return;
    const dx = Math.abs(x - pressStartPos.current.x);
    const dy = Math.abs(y - pressStartPos.current.y);
    if (dx > 10 || dy > 10) handlePressEnd();
  };

  const handlePressEnd = () => {
    pressStartPos.current = null;
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  let bubble: ReactNode = null;

  if (isOwn) {
    bubble = (
      <UserMessage
        attachments={<MessageAttachment item={item} />}
        message={item.message}
        time={time}
        isRead={item.recipients?.some(recipient => recipient.read) ?? false}
        isEdited={isEdited}
        isDeleted={isDeleted}
      />
    );
  }

  if (!isOwn && discussionType === DiscussionType.PRIVATE) {
    bubble = (
      <PrivateMessage
        attachments={<MessageAttachment item={item} />}
        message={item.message}
        time={time}
        isEdited={isEdited}
        isDeleted={isDeleted}
      />
    );
  }

  if (!isOwn && discussionType && discussionType !== DiscussionType.PRIVATE) {
    bubble = (
      <IncomingMessage
        attachments={<MessageAttachment item={item} />}
        message={item.message}
        time={time}
        senderName={item.senderName}
        senderAvatar={item.senderAvatar}
        isEdited={isEdited}
        isDeleted={isDeleted}
      />
    );
  }

  if (!bubble) return null;

  return (
    <div
      className="relative select-none"
      onTouchStart={e =>
        handlePressStart(e.touches[0].clientX, e.touches[0].clientY)
      }
      onTouchMove={e =>
        handlePressMove(e.touches[0].clientX, e.touches[0].clientY)
      }
      onTouchEnd={handlePressEnd}
      onTouchCancel={handlePressEnd}
      onMouseDown={e => handlePressStart(e.clientX, e.clientY)}
      onMouseMove={e => handlePressMove(e.clientX, e.clientY)}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      onContextMenu={e => {
        if (!onSelect || isDeleted) return;
        e.preventDefault();
        if (!longPressFired.current) onSelect(item);
        longPressFired.current = false;
      }}
    >
      {bubble}
      {isSelected && (
        <div className="absolute inset-0 pointer-events-none bg-blue-selection" />
      )}
    </div>
  );
};

export default ChatMessageItem;
